import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { userLoginTypes } from '../../types/userTypes';
import { Doc } from 'prettier';
import { Permission } from '../../components/Admin/UpdatePermissions/RolesSection';
import type { User } from '../../types/userTypes';
import { AUTH_URL } from '../../util/constants';
type leaveData = {
  leave_data: {}[];
  role: string | undefined;
  name: string | undefined;
  email: string | undefined;
};
type deleteEmployee = {
  id: string;
};

type activateDeactiavateEmployee = {
  id: string;
};
type DocumentFieldUpdate = {
  id: string;
  type: string;
};

interface Employees {
  _id: string;
  name: string;
  dob: string;
  slack_id: string;
  id: string;
  avatar: string;
}

interface RolesEmployees {
  _id: string;
  name: string;
  role: string;
  avatar: string;
}

interface Roles {
  _id: string;
  roleName: string;
  employees: RolesEmployees[];
}


const baseQueryWithAuth = async (args: any, api: any, extraOptions: any) => {
  const result = await fetchBaseQuery({
    baseUrl: AUTH_URL,
  })(args, api, extraOptions);

  if (result.error && result.error.status === 403) {
    window.location.href = '/';
  }

  return result;
};

// const AUTH_URL = process.env.REACT_APP_PROD_AUTH_URL;

export const authApi = createApi({
  reducerPath: 'authApi',
  // keepUnusedDataFor: 3,
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'queryAllusers',
    'queryRoles',
    'mutateRole',
    'userById',
    'getCurrentUser',
    'allUsersById',
  ],
  endpoints: (builder) => ({
    userSignup: builder.mutation<
      {
        message: string;
        status: boolean;
      },
      {
        email: string | undefined;
        employment_type: string | undefined;
        hr: string | undefined;
        attendancePassword: string | undefined;
        slack_id: string | undefined;
        name: string | undefined;
      }
    >({
      query: (userdata) => ({
        url: '/register',
        method: 'POST',
        body: userdata,
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
    }),
    userLogin: builder.mutation<any, userLoginTypes>({
      query: (userdata) => ({
        url: '/login',
        method: 'POST',
        body: userdata,
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: ['getCurrentUser'],
    }),

    getRoles: builder.query<
      { status: string; log: string; roles: any[] },
      void
    >({
      query: () => ({
        url: '/roles',
        credentials: 'include',
      }),
      providesTags: ['queryRoles'],
    }),

    editRole: builder.mutation<
      any,
      { roleId: string; permissions: Permission[] }
    >({
      query: ({ roleId, permissions }) => ({
        url: `/roles/${roleId}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { permissions },
        credentials: 'include',
      }),
    }),

    createRole: builder.mutation<
      any,
      { roleName: string; permissions: Permission[] }
    >({
      query: (newRole) => ({
        url: '/roles',
        method: 'POST',
        body: newRole,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }),
    }),

    updateRole: builder.mutation({
      query: ({ employeeId, role }) => ({
        url: `/roles/${employeeId}`,
        method: 'PATCH',
        body: { role },
        credentials: 'include',
      }),
      invalidatesTags: ['allUsersById'],
    }),

    getAllusers: builder.query<
      { status: string; log: string; employee: any[] },
      void
    >({
      query: () => ({
        url: '/getEmployees',
        credentials: 'include',
      }),
      keepUnusedDataFor: 0,
    }),

    getAllUsersById: builder.query({
      query: (ids) => ({
        url: '/employees/multiple-payslip',
        method: 'GET',
        body: ids,
        credentials: 'include',
      }),
      providesTags: ['allUsersById'],
    }),
    changeRole: builder.mutation<
      any,
      { email: string | undefined; role: string }
    >({
      query: (role) => ({
        url: '/updateRole',
        method: 'POST',
        body: role,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'userById'],
    }),
    changeEmploymentType: builder.mutation<
      any,
      { email: string | undefined; employment_type: string }
    >({
      query: (employment_type) => ({
        url: '/updateEmploymentType',
        method: 'POST',
        body: employment_type,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'userById'],
    }),
    getCurrentUser: builder.query<
      { status: string; log: string; User: User },
      void
    >({
      query: () => ({
        url: '/me',
        method: 'GET',
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
      providesTags: ['getCurrentUser'],
    }),

    getVerifiedUsersBirthday: builder.query<
      { status: string; log: string; employees: Employees[] },
      void
    >({
      query: () => ({
        url: '/getVerifiedUsersBirthday',
        method: 'GET',
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
    }),
    getRolesEmployees: builder.query<
      { status: string; log: string; roles: Roles[] },
      void
    >({
      query: () => ({
        url: '/rolesEmployees',
        method: 'GET',
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
    }),
    getUserById: builder.query<any, { _id: string | undefined | null }>({
      query: ({ _id }) => ({
        url: `/employee/${_id}`,
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
      providesTags: ['userById'],
    }),

    changeManager: builder.mutation<
      any,
      { email: string | undefined; manager_id: string | undefined }
    >({
      query: ({ email, manager_id }) => ({
        url: '/assignManager',
        method: 'POST',
        body: { email, manager_id },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'userById'],
    }),
    changeAdmin: builder.mutation<
      any,
      { email: string | undefined; admin_id: string | undefined }
    >({
      query: ({ email, admin_id }) => ({
        url: '/changeAdmin',
        method: 'POST',
        body: { email, admin_id },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'userById'],
    }),
    chnagePassword: builder.mutation<
      any,
      {
        email: string | undefined;
        password: string | undefined;
        new_password: string | undefined;
      }
    >({
      query: ({ email, password, new_password }) => ({
        url: '/changePassword',
        method: 'POST',
        body: {
          email,
          password,
          newPassword: new_password,
        },
        credentials: 'include',
      }),
    }),
    forgotPassword: builder.mutation<
      any,
      {
        email: string | undefined;
      }
    >({
      query: ({ email }) => ({
        url: '/forgotpassword',
        method: 'POST',
        body: {
          email,
        },
        credentials: 'include',
      }),
    }),
    resetPassword: builder.mutation<
      any,
      {
        token: string;
        newPassword: string | undefined;
      }
    >({
      query: ({ token, newPassword }) => ({
        url: `/reset-password`,
        method: 'POST',
        body: {
          newPassword,
          token,
        },
        credentials: 'include',
      }),
    }),
    logout: builder.mutation<any, void>({
      query: () => ({
        url: '/logout',
        method: 'DELETE',
        credentials: 'include',
      }),
      // invalidatesTags: ["getCurrentUser"],
    }),
    uploadDocuments: builder.mutation<any, { formData: FormData }>({
      query: ({ formData }) => ({
        url: '/uploadDocuments',
        method: 'POST',
        body: formData,
        credentials: 'include',
      }),
    }),
    updateDocuments: builder.mutation<any, { formData: any }>({
      query: ({ formData }) => ({
        url: '/uploadDocuments',
        method: 'PUT',
        body: formData,
        credentials: 'include',
      }),
    }),
    updateField: builder.mutation<any, { body: DocumentFieldUpdate }>({
      query: ({ body }) => ({
        url: '/uploadDocuments',
        method: 'PATCH',
        body,
        credentials: 'include',
      }),
    }),
    updatePersonalInfo: builder.mutation<any, { data: any }>({
      query: ({ data }) => ({
        url: '/uploadPeronsalDetails',
        method: 'POST',
        body: data,
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
      }),
    }),
    verifyUser: builder.mutation<
      any,
      { email: string | undefined; status: string | undefined }
    >({
      query: ({ email, status }) => ({
        url: '/verify',
        method: 'POST',
        body: {
          email,
          status,
        },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers'],
    }),
    // not using this route
    downloadPdf: builder.query<void, { s3_url: string | undefined }>({
      query: ({ s3_url }) => ({
        url: `${AUTH_URL}/download/?id=${s3_url}`,
        credentials: 'include',
        method: 'GET',
        headers: {
          'content-type': 'application/pdf',
        },
      }),
    }),
    getDocuments: builder.query<{ list: Doc[] }, { userId: string }>({
      query: ({ userId }) => ({
        url: `${AUTH_URL}/documents/?userId=${userId}`,
        credentials: 'include',
        method: 'GET',
      }),
    }),
    changeLeave: builder.mutation<any, leaveData>({
      query: (leaveData) => ({
        url: '/changeLeaveData',
        method: 'POST',
        body: leaveData,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'getCurrentUser'],
    }),
    getAllEmployees: builder.query<any, void>({
      query: () => ({
        url: '/all',
        credentials: 'include',
        method: 'GET',
      }),
      providesTags: ['queryAllusers'],
    }),
    addResignation: builder.mutation<any, any>({
      query: (data) => ({
        url: '/resign',
        method: 'POST',
        body: data,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'getCurrentUser'],
    }),
    getOffboardingEmployee: builder.query<any, any>({
      query: (employeeId) => ({
        url: `/resign/${employeeId}`,
        credentials: 'include',
      }),
    }),
    getAllOffboardingEmployees: builder.query<any, void>({
      query: () => ({
        url: `/resign`,
        credentials: 'include',
      }),
    }),
    getEmployeeOffBoardingProject: builder.query<any, any>({
      query: (employeeId) => ({
        url: `/resign/project/${employeeId}`,
        credentials: 'include',
      }),
    }),
    addProject: builder.mutation<any, any>({
      query: (data) => ({
        url: `/resign/project`,
        method: 'POST',
        body: data,
        credentials: 'include',
      }),
    }),
    updateProjectStatus: builder.mutation<any, any>({
      query: ({ status, projectId }) => ({
        url: `/resign/project/${projectId}`,
        method: 'PUT',
        body: { status },
        credentials: 'include',
      }),
    }),
    employeeOffBoardingUpdate: builder.mutation<any, any>({
      query: ({ data, employeeId }) => ({
        url: `/resign/${employeeId}`,
        method: 'PATCH',
        body: data,
        credentials: 'include',
      }),
    }),
    updateLastDay: builder.mutation<any, any>({
      query: ({ date, employeeId }) => ({
        url: `/resign/date/${employeeId}`,
        method: 'PUT',
        body: { date },
        credentials: 'include',
      }),
    }),
    updateResignationStatus: builder.mutation<any, any>({
      query: ({ status, employeeId }) => ({
        url: `/resign/status/${employeeId}`,
        method: 'PUT',
        body: { status },
        credentials: 'include',
      }),
    }),
    uploadExitForm: builder.mutation<any, any>({
      query: (formData) => ({
        url: `/resign/uploadExitForm`,
        method: 'POST',
        body: formData,
        credentials: 'include',
      }),
    }),
    addManualDocument: builder.mutation<any, any>({
      query: (formData) => ({
        url: `/resign/manual`,
        method: 'POST',
        body: formData,
        credentials: 'include',
      }),
    }),
    getEmployeeDetail: builder.query<any, any>({
      query: ({ employeeId }) => ({
        url: `/resign/detail/${employeeId}`,
        credentials: 'include',
      }),
    }),
    getAllManualDocs: builder.query<any, void>({
      query: () => ({
        url: `/resign/manual`,
        credentials: 'include',
      }),
    }),
    getPeopleData: builder.query<any, void>({
      query: () => ({
        url: `/people`,
        credentials: 'include',
      }),
    }),
    getLeaveAndRegularizationRequests: builder.query<any, void>({
      query: () => ({
        url: `/people/leave`,
        credentials: 'include',
      }),
    }),
    getTodayAttendance: builder.query<any, void>({
      query: () => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return {
          url: `/people/list?timezone=${encodeURIComponent(timezone)}`,
          credentials: 'include',
        };
      },
    }),
    getEmployeeOverview: builder.query<any, any>({
      query: (employeeId) => ({
        url: `/overview/${employeeId}`,
        credentials: 'include',
      }),
    }),
    getMonthlyHours: builder.query<any, any>({
      query: ({ employeeId, timezone }) => ({
        url: `/employoyee/hours/${employeeId}?timezone=${timezone}`,
        method: 'GET',
        credentials: 'include',
      }),
    }),
    deleteEmployee: builder.mutation<any, deleteEmployee>({
      query: (employee) => ({
        url: `/delete/${employee.id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'getCurrentUser'],
    }),
    updateSlackId: builder.mutation<
      any,
      { slack_id: string; employee_id: string }
    >({
      query: ({ slack_id, employee_id }) => ({
        url: '/updateSlackId',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: { slack_id, employee_id },
        credentials: 'include',
      }),
    }),
    getAllEmployeeLeaveBalances: builder.query<any, void>({
      query: () => ({
        url: `/employees/leaves`,
        credentials: 'include',
      }),
    }),
    uploadLeaveData: builder.mutation<any, any>({
      query: (data) => ({
        url: '/employees/leaves',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data,
        credentials: 'include',
      }),
    }),
    activateDeactiavateEmployee: builder.mutation<
      any,
      activateDeactiavateEmployee
    >({
      query: (employee) => ({
        url: `/activate-deactivate/${employee.id}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      invalidatesTags: ['queryAllusers', 'getCurrentUser'],
    }),
  }),
});

export const {
  useUserLoginMutation,
  useUserSignupMutation,
  useGetRolesQuery,
  useEditRoleMutation,
  useUpdateRoleMutation,
  useCreateRoleMutation,
  useGetAllusersQuery,
  useGetAllUsersByIdQuery,
  useGetVerifiedUsersBirthdayQuery,
  useGetRolesEmployeesQuery,
  useChangeRoleMutation,
  useChangeEmploymentTypeMutation,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useUpdateSlackIdMutation,
  useChangeAdminMutation,
  useChangeManagerMutation,
  useChnagePasswordMutation,
  useLazyGetCurrentUserQuery,
  useGetCurrentUserQuery,
  useLogoutMutation,
  useUploadDocumentsMutation,
  useUpdatePersonalInfoMutation,
  useVerifyUserMutation,
  useDownloadPdfQuery,
  useChangeLeaveMutation,
  useDeleteEmployeeMutation,
  useGetDocumentsQuery,
  useUpdateDocumentsMutation,
  useUpdateFieldMutation,
  useActivateDeactiavateEmployeeMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetAllEmployeesQuery,
  useAddResignationMutation,
  useLazyGetOffboardingEmployeeQuery,
  useGetAllOffboardingEmployeesQuery,
  useAddProjectMutation,
  useLazyGetEmployeeOffBoardingProjectQuery,
  useUpdateProjectStatusMutation,
  useEmployeeOffBoardingUpdateMutation,
  useUpdateLastDayMutation,
  useLazyGetEmployeeDetailQuery,
  useUpdateResignationStatusMutation,
  useUploadExitFormMutation,
  useAddManualDocumentMutation,
  useGetAllManualDocsQuery,
  useLazyGetPeopleDataQuery,
  useLazyGetTodayAttendanceQuery,
  useLazyGetMonthlyHoursQuery,
  useLazyGetLeaveAndRegularizationRequestsQuery,
  useLazyGetAllusersQuery,
  useLazyGetEmployeeOverviewQuery,
  useGetAllEmployeeLeaveBalancesQuery,
  useUploadLeaveDataMutation,
} = authApi;
